import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  ChevronDown,
  Circle,
  Download,
  Loader2,
  RefreshCw,
  RotateCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';

import {
  formatBytes,
  type Run,
  type RunFile,
  type RunId,
  type RunStep,
  type RestartRequestStepName,
  type StepName,
} from '@grafx-vdp/shared';

import { useAuth } from '@/api/auth-context.js';
import { queryKeys, useApiMetaQuery, useCancelRunMutation, useDeleteRunMutation, useRestartRunMutation, useRunDatasetQuery, useRunFilesQuery, useRunQuery } from '@/api/hooks.js';
import { downloadRunFile } from '@/api/runs.js';
import { toast } from '@/components/ui/sonner.js';
import { Button } from '@/components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { RunStatusBadge, StepPlanStatusBadge, type StepPlanState } from '@/lib/status.js';
import {
  STEP_PLAN,
  normalizeStepName,
  resolveRunFailedStep,
  resolveStepRestartOffer,
  restartOfferLabel,
  resolveStepDetailValue,
  stepPlanLabel,
  type StepDatasetFacts,
  type StepDetailContext,
  type StepDetailField,
  type StepPlanEntry,
} from '@/lib/steps.js';
import { formatAbsolute, formatDuration, formatRelative } from '@/lib/time.js';
import { CopyableValue, MetadataField } from '@/components/CopyableValue.js';
import { cn } from '@/lib/utils.js';

const DATASET_PAGE_SIZE = 25;

export function RunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const queryClient = useQueryClient();
  const query = useRunQuery(runId);
  const metaQuery = useApiMetaQuery();
  const canDeleteRun = metaQuery.data?.capabilities.deleteRun ?? false;
  const isRefreshing =
    useIsFetching({ queryKey: runId ? queryKeys.run(runId as RunId) : ['run', 'none'] }) > 0;

  const handleRefresh = () => {
    if (!runId) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.run(runId as RunId) });
  };

  if (query.isLoading) return <DetailSkeleton />;
  if (query.isError || !query.data) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-6 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          Failed to load run {runId}.
        </CardContent>
      </Card>
    );
  }

  const { run, steps } = query.data;

  const inputFilename =
    run.inputFilename ??
    (steps.find((s) => s.stepName === 'pickup' && s.detail?.filename)?.detail?.filename as
      | string
      | undefined);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2 text-muted-foreground">
            <Link to="/runs">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to runs
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Run</h1>
          <div className="mt-1">
            <CopyableValue value={run.id} />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Flow <span className="font-mono">{run.flowId}</span> · {formatRelative(run.startedAt)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn('mr-1 h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
          {(run.status === 'running' || run.status === 'queued') && runId ? (
            <CancelRunButton runId={runId} />
          ) : null}
          {canDeleteRun && runId ? <DeleteRunButton runId={runId} /> : null}
          <RunStatusBadge status={run.status} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">Metadata</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
          <MetadataField label="Trigger" value={run.triggerSource} />
          <MetadataField label="Started" value={formatAbsolute(run.startedAt)} />
          <MetadataField label="Ended" value={formatAbsolute(run.endedAt)} />
          <MetadataField label="Duration" value={formatDuration(run.durationMs)} />
          <MetadataField label="Template" value={run.templateId} copyable />
          <MetadataField label="Dataset" value={run.datasetId} copyable />
          <MetadataField
            label="Input file"
            value={inputFilename ?? ''}
            copyable={Boolean(inputFilename)}
          />
          <MetadataField
            label="Input ref"
            value={run.inputRef ?? ''}
            mono
            copyable={Boolean(run.inputRef)}
          />
          {run.rowCount != null ? (
            <MetadataField label="Data rows" value={String(run.rowCount)} />
          ) : null}
        </CardContent>
      </Card>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-muted-foreground">Steps</h2>
        <StepsSection run={run} steps={steps} runId={runId} />
      </section>

      {runId ? <ImportedDataCard runId={runId} /> : null}

      {run.errorSummary && !STEP_PLAN.some((e) => e.stepName === run.errorSummary!.step) ? (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-destructive">
              <XCircle className="h-4 w-4" />
              Error summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="text-xs uppercase text-muted-foreground">Step</span>{' '}
              <span className="font-mono">{run.errorSummary.step}</span>
            </div>
            <div>
              <span className="text-xs uppercase text-muted-foreground">Attempts</span>{' '}
              {run.errorSummary.attempts}
            </div>
            <pre className="overflow-auto rounded-md bg-muted p-3 text-xs">
              {run.errorSummary.message}
            </pre>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function ImportedDataCard({ runId }: { runId: RunId }) {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const query = useRunDatasetQuery(runId, offset, DATASET_PAGE_SIZE);

  const total = query.data?.total ?? 0;
  const columns = query.data?.columns ?? [];
  const rows = query.data?.rows ?? [];
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + DATASET_PAGE_SIZE, total);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-6 py-4 text-left"
      >
        <CardTitle className="flex-1 text-sm font-medium text-muted-foreground">
          Imported data
        </CardTitle>
        {!query.isLoading && total > 0 ? (
          <span className="text-xs text-muted-foreground">
            {total} row{total === 1 ? '' : 's'} · {columns.length} column
            {columns.length === 1 ? '' : 's'}
          </span>
        ) : null}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open ? (
        <CardContent className="space-y-4 border-t pt-4 text-sm">
        {query.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : query.isError ? (
          <p className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            Failed to load imported data.
          </p>
        ) : total === 0 ? (
          <p className="text-muted-foreground">
            No imported rows. The dataset is empty until the persist-rows step runs, and is removed
            once the run cleans up.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-muted-foreground">
              <span>
                <span className="font-medium text-foreground">{total}</span> row
                {total === 1 ? '' : 's'}
              </span>
              <span>
                <span className="font-medium text-foreground">{columns.length}</span> column
                {columns.length === 1 ? '' : 's'}
              </span>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((col) => (
                    <TableHead key={col} className="whitespace-nowrap font-mono text-xs">
                      {col}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((col) => (
                      <TableCell key={col} className="whitespace-nowrap font-mono text-xs">
                        {formatCell(row.data[col])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Showing {pageStart}–{pageEnd} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - DATASET_PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageEnd >= total}
                  onClick={() => setOffset((o) => o + DATASET_PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
        </CardContent>
      ) : null}
    </Card>
  );
}

function formatCell(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value === '') return '—';
  return String(value);
}

function StepsSection({
  run,
  steps,
  runId,
}: {
  run: Run;
  steps: RunStep[];
  runId: RunId | undefined;
}) {
  // Lightweight dataset facts (total + columns) used as a fallback source for
  // the parse/persist detail fields until the backend records them per-step.
  const datasetQuery = useRunDatasetQuery(runId, 0, 1);
  const datasetFacts: StepDatasetFacts | null = datasetQuery.data
    ? {
        total: datasetQuery.data.total,
        columnCount: datasetQuery.data.columns.length,
        datasetId: datasetQuery.data.datasetId,
      }
    : null;

  // Downloadable artifacts the run staged in R2, grouped by the step that
  // produced them (input CSV, generated PDFs, …).
  const filesQuery = useRunFilesQuery(runId);
  const filesByStep = useMemo(() => {
    const map = new Map<StepName, RunFile[]>();
    for (const file of filesQuery.data?.files ?? []) {
      const stepName = normalizeStepName(file.stepName);
      const list = map.get(stepName) ?? [];
      list.push(file);
      map.set(stepName, list);
    }
    return map;
  }, [filesQuery.data]);
  const preparedDeliveryFiles = filesByStep.get('prepare-delivery') ?? [];
  const download = useFileDownloader(runId);

  const attemptsByName = useMemo(() => {
    const map = new Map<StepName, RunStep[]>();
    for (const step of steps) {
      const stepName = normalizeStepName(step.stepName);
      const list = map.get(stepName) ?? [];
      list.push(step);
      map.set(stepName, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.attempt - b.attempt);
    return map;
  }, [steps]);

  // The "currently running" step: when the run is in-flight, the first planned
  // step that has not succeeded yet. Simple and robust.
  const currentStepName = useMemo<StepName | null>(() => {
    if (run.status !== 'running') return null;
    for (const entry of STEP_PLAN) {
      const attempts = attemptsByName.get(entry.stepName);
      const latest = attempts && attempts.length > 0 ? attempts[attempts.length - 1] : undefined;
      if (!latest || latest.status !== 'succeeded') return entry.stepName;
    }
    return null;
  }, [run.status, attemptsByName]);

  const failedAtStep = useMemo(
    () => (run.status === 'failed' ? resolveRunFailedStep(run, attemptsByName) : null),
    [run, attemptsByName],
  );

  return (
    <ol className="space-y-2">
      {STEP_PLAN.map((entry, index) => {
        const attempts = attemptsByName.get(entry.stepName) ?? [];
        const latest = attempts.length > 0 ? attempts[attempts.length - 1] ?? null : null;
        const state = deriveStepState(latest, entry.stepName === currentStepName, run.status);
        const ctx: StepDetailContext = { latest, attempts, run, dataset: datasetFacts };
        const restartOffer = resolveStepRestartOffer(run, entry.stepName, state, attemptsByName);
        return (
          <li key={entry.stepName}>
            <StepRow
              entry={entry}
              index={index}
              attempts={attempts}
              state={state}
              ctx={ctx}
              files={filesByStep.get(entry.stepName) ?? []}
              deliveredFiles={entry.stepName === 'deliver' ? preparedDeliveryFiles : []}
              download={download}
              runId={runId}
              restartOffer={restartOffer}
              failedAtStep={failedAtStep}
            />
          </li>
        );
      })}
    </ol>
  );
}

function deriveStepState(
  latest: RunStep | null,
  isCurrent: boolean,
  runStatus: Run['status'],
): StepPlanState {
  if (!latest) return isCurrent ? 'running' : 'pending';
  if (latest.status === 'succeeded') return 'succeeded';
  if (latest.status === 'failed') {
    // After a manual restart (or Workflow step retries) the run row is `running`
    // while earlier failed attempts remain in `run_steps`. Show in-flight, not failed.
    if (isCurrent && runStatus === 'running') return 'running';
    return 'failed';
  }
  // `retrying` means the step is mid-flight working through attempts.
  return 'running';
}

function StepRow({
  entry,
  index,
  attempts,
  state,
  ctx,
  files,
  deliveredFiles,
  download,
  runId,
  restartOffer,
  failedAtStep,
}: {
  entry: StepPlanEntry;
  index: number;
  attempts: RunStep[];
  state: StepPlanState;
  ctx: StepDetailContext;
  files: RunFile[];
  deliveredFiles: RunFile[];
  download: FileDownloader;
  runId: RunId | undefined;
  restartOffer: { fromStep: RestartRequestStepName; usesEarlierCheckpoint: boolean } | null;
  failedAtStep: StepName | null;
}) {
  const isFailed = state === 'failed';
  // Auto-expand a failed step so its error is visible without a click.
  const [open, setOpen] = useState(isFailed);
  const latest = ctx.latest;
  const isPending = state === 'pending';

  // Prefer the run-level error message when this is the step that failed the
  // run (it is the fuller, authoritative message); otherwise fall back to the
  // latest attempt's recorded excerpt.
  const errorMessage = isFailed
    ? (failedAtStep === entry.stepName ? ctx.run.errorSummary?.message : null) ??
      latest?.errorExcerpt ??
      null
    : null;

  // For repeating steps (poll), prefer the recorded `detail.checks` count — a
  // single `trackDirect` row records all polls on one step row, so the recorded
  // attempt count would otherwise read "1". Fall back to the row count.
  const recordedChecks =
    typeof latest?.detail?.checks === 'number' ? latest.detail.checks : attempts.length;
  const repeatLabel =
    entry.repeats && attempts.length > 0
      ? `Checked ${recordedChecks} time${recordedChecks === 1 ? '' : 's'}`
      : null;

  return (
    <Card
      className={cn(
        isPending && 'opacity-70',
        state === 'failed' && 'border-destructive/40',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start gap-3 px-6 py-4 text-left"
      >
        <StepStateIcon state={state} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-medium">
              {index + 1}. {entry.label}
            </span>
            <span className="font-mono text-xs text-muted-foreground">{entry.stepName}</span>
            {repeatLabel ? (
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                {repeatLabel}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{entry.description}</p>
          {latest ? (
            <div className="mt-1 text-xs text-muted-foreground">
              {state === 'running' && ctx.run.status === 'running' && latest.status === 'failed'
                ? 'Retry in progress…'
                : `${formatAbsolute(latest.startedAt)} · ${formatDuration(latest.durationMs)}`}
              {attempts.length > 1 ? ` · ${attempts.length} prior attempts` : ''}
            </div>
          ) : state === 'running' ? (
            <div className="mt-1 text-xs text-muted-foreground">In progress…</div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {restartOffer && runId && state === 'succeeded' ? (
            <RestartFromStepButton
              runId={runId}
              fromStep={restartOffer.fromStep}
              label={restartOfferLabel(restartOffer)}
              compact
            />
          ) : null}
          <StepPlanStatusBadge state={state} />
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </div>
      </button>

      {isFailed ? (
        <div className="border-t border-destructive/30 bg-destructive/5 px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-destructive">
              <XCircle className="h-3.5 w-3.5" />
              Failed at this step
              {attempts.length > 1 ? ` · ${attempts.length} attempts` : ''}
            </div>
            {restartOffer && runId ? (
              <RestartFromStepButton
                runId={runId}
                fromStep={restartOffer.fromStep}
                label={restartOfferLabel(restartOffer)}
              />
            ) : null}
          </div>
          {errorMessage ? (
            <pre className="mt-2 overflow-auto rounded-md bg-destructive/10 p-3 text-xs text-destructive">
              {errorMessage}
            </pre>
          ) : null}
        </div>
      ) : null}

      {open ? (
        <CardContent className="space-y-4 border-t pt-4 text-sm">
          <p className="text-muted-foreground">{entry.description}</p>

          {attempts.length > 0 ? (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {entry.repeats ? 'Checks' : 'Attempts'}
              </div>
              <ul className="space-y-2">
                {attempts.map((attempt) => (
                  <AttemptRow key={attempt.id} attempt={attempt} repeats={entry.repeats} />
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">This step has not run yet.</p>
          )}

          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Details
            </div>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {entry.detailFields.map((field) => {
                if (entry.stepName === 'deliver' && field.key === 'fileCount') {
                  return (
                    <DeliveredFilesDetail
                      key={field.key}
                      label={field.label}
                      files={deliveredFiles}
                      fallback={resolveStepDetailValue(entry.stepName, field.key, ctx)}
                    />
                  );
                }
                const value = resolveStepDetailValue(entry.stepName, field.key, ctx);
                // Optional fields (e.g. the error report) only render when present.
                if (field.optional && value === null) return null;
                return <DetailFieldRow key={field.key} field={field} value={value} />;
              })}
            </dl>
          </div>

          {files.length > 0 ? (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Files
              </div>
              <ul className="space-y-1.5">
                {files.map((file) => (
                  <li
                    key={file.key}
                    className="flex items-center justify-between gap-3 rounded-md border p-2 pl-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs">{file.name}</div>
                      <div className="text-xs text-muted-foreground">{formatBytes(file.sizeBytes)}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={download.pendingKey === file.key}
                      onClick={() => download.start(file)}
                    >
                      {download.pendingKey === file.key ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                      Download
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}

interface FileDownloader {
  pendingKey: string | null;
  start: (file: RunFile) => void;
}

/**
 * Authenticated download helper: fetches a run artifact as a blob (carrying the
 * JWT a plain `<a href>` couldn't send) and triggers a browser save. Tracks the
 * in-flight key so the originating button can show a spinner.
 */
function useFileDownloader(runId: RunId | undefined): FileDownloader {
  const { jwt } = useAuth();
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const start = useCallback(
    (file: RunFile) => {
      if (!runId) return;
      setPendingKey(file.key);
      void (async () => {
        try {
          await downloadRunFile(jwt, runId, file.key, {
            filename: file.name,
            sizeBytes: file.sizeBytes,
          });
        } catch (err) {
          toast.error(`Failed to download ${file.name}`, {
            description: err instanceof Error ? err.message : String(err),
          });
        } finally {
          setPendingKey(null);
        }
      })();
    },
    [jwt, runId],
  );

  return { pendingKey, start };
}

function AttemptRow({ attempt, repeats }: { attempt: RunStep; repeats?: boolean }) {
  return (
    <li className="rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {repeats ? 'Check' : 'Attempt'} {attempt.attempt} · {formatAbsolute(attempt.startedAt)} ·{' '}
          {formatDuration(attempt.durationMs)}
        </span>
        <StepStateIcon state={attempt.status === 'failed' ? 'failed' : attempt.status === 'succeeded' ? 'succeeded' : 'retrying'} />
      </div>
      {attempt.errorExcerpt ? (
        <pre className="mt-2 overflow-auto rounded-md bg-muted p-3 text-xs">
          {attempt.errorExcerpt}
        </pre>
      ) : null}
    </li>
  );
}

function DeliveredFilesDetail({
  label,
  files,
  fallback,
}: {
  label: string;
  files: RunFile[];
  fallback: string | null;
}) {
  return (
    <div className="sm:col-span-2">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">
        {files.length > 0 ? (
          <ul className="list-disc space-y-0.5 pl-5">
            {files.map((file) => (
              <li key={file.key} className="break-all font-mono text-xs">
                {file.name}
              </li>
            ))}
          </ul>
        ) : (
          <span className={cn(!fallback && 'text-muted-foreground')}>{fallback ?? '—'}</span>
        )}
      </dd>
    </div>
  );
}

function DetailFieldRow({ field, value }: { field: StepDetailField; value: string | null }) {
  if (field.kind === 'json') {
    return (
      <div className="sm:col-span-2">
        <dt className="text-xs uppercase tracking-wide text-muted-foreground">{field.label}</dt>
        <dd className="mt-1">
          {value === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <pre className="overflow-auto rounded-md bg-muted p-3 text-xs">{value}</pre>
          )}
        </dd>
      </div>
    );
  }

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{field.label}</dt>
      <dd className={cn('mt-0.5 break-words', value === null && 'text-muted-foreground')}>
        {value ?? '—'}
      </dd>
    </div>
  );
}

function StepStateIcon({ state }: { state: StepPlanState }) {
  switch (state) {
    case 'succeeded':
      return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--palette-success-dark)]" />;
    case 'failed':
      return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />;
    case 'running':
      return (
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[var(--palette-info-dark)]" />
      );
    case 'retrying':
      return <RotateCw className="mt-0.5 h-4 w-4 shrink-0 text-[var(--palette-warning-dark)]" />;
    case 'pending':
    default:
      return <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />;
  }
}

function RestartFromStepButton({
  runId,
  fromStep,
  label,
  compact = false,
}: {
  runId: RunId;
  fromStep: RestartRequestStepName;
  label: string;
  compact?: boolean;
}) {
  const restart = useRestartRunMutation();
  const [open, setOpen] = useState(false);

  const handleRestart = async () => {
    try {
      await restart.mutateAsync({ runId, fromStep });
      toast.success('Run restarted', {
        description:
          fromStep === 'poll-output-task'
            ? 'Resuming poll for the existing GraFx output task.'
            : `Resuming from ${stepPlanLabel(fromStep)}.`,
      });
      setOpen(false);
    } catch (e) {
      toast.error('Restart failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  const dialogBody =
    fromStep === 'poll-output-task' ? (
      <>
        The workflow will resume polling the existing GraFx output task. No new task will be
        submitted to GraFx Studio.
      </>
    ) : (
      <>
        The workflow will resume from{' '}
        <span className="font-medium text-foreground">{stepPlanLabel(fromStep)}</span>. Completed
        steps before that checkpoint are reused; this step and everything after it will run again.
      </>
    );

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={cn(
          compact && 'h-7 px-2 text-xs',
          !compact && 'border-destructive/40 text-destructive hover:bg-destructive/10',
        )}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        disabled={restart.isPending}
      >
        {restart.isPending ? (
          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
        ) : (
          <RotateCw className="mr-1 h-3.5 w-3.5" />
        )}
        {label}
      </Button>
      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !restart.isPending && setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Restart this run?</h2>
            <p className="mt-2 text-sm text-muted-foreground">{dialogBody}</p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={restart.isPending}>
                Cancel
              </Button>
              <Button onClick={handleRestart} disabled={restart.isPending}>
                {restart.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Restart run
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function DeleteRunButton({ runId }: { runId: RunId }) {
  const navigate = useNavigate();
  const remove = useDeleteRunMutation();
  const [open, setOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(runId);
      toast.success('Run deleted');
      setOpen(false);
      navigate('/runs');
    } catch (e) {
      toast.error('Delete failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-destructive/40 text-destructive hover:bg-destructive/10"
        onClick={() => setOpen(true)}
        disabled={remove.isPending}
      >
        {remove.isPending ? (
          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Trash2 className="mr-1 h-3.5 w-3.5" />
        )}
        Delete run
      </Button>
      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
            <h2 className="text-lg font-semibold">Delete this run?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This permanently removes the run record, step history, imported dataset rows, and all
              staged files in storage. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={remove.isPending}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={remove.isPending}>
                {remove.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Delete run
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function CancelRunButton({ runId }: { runId: RunId }) {
  const cancel = useCancelRunMutation();
  const [open, setOpen] = useState(false);

  const handleCancel = async () => {
    try {
      await cancel.mutateAsync(runId);
      toast.success('Run cancelled');
      setOpen(false);
    } catch (e) {
      toast.error('Cancel failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={cancel.isPending}>
        {cancel.isPending ? (
          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Ban className="mr-1 h-3.5 w-3.5" />
        )}
        Cancel run
      </Button>
      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
            <h2 className="text-lg font-semibold">Cancel this run?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              The workflow will stop as soon as possible. Steps already completed stay recorded; no
              further output will be generated.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={cancel.isPending}>
                Keep running
              </Button>
              <Button variant="destructive" onClick={handleCancel} disabled={cancel.isPending}>
                {cancel.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Cancel run
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
